// The netti inbox as a value: one pure reducer every change goes through, so
// the list, the tab's count and the optimistic presses (seen, unveil, dismiss,
// react) can never disagree. No DOM, no fetch, no clock: the hook
// (useInbox.ts) owns the side effects and hands their answers in here.

import type { InboxItem, InboxPayload } from "../../lib/inbox/types";
import type { RingAsks, RingRequest } from "../../lib/self/ringView";
import type { GrantLength } from "../../lib/self/ring";
import type { Category } from "../../lib/self/categories";
import type { RecordKind, RecordRef } from "../../lib/you/record";

export type InboxError = "auth" | "missing" | "failed";
export type AskDecision = "allowed_once" | "allowed_always" | "denied";
/** an answer's length and what it covers (149); none = 128's answer */
export interface AskHow {
  length?: GrantLength;
  categories?: Category[] | null;
}

/** a request the person answered here: it stays in place, saying what happened,
 *  until they fold it away (or the page is left) */
export interface Answered {
  req: RingRequest;
  decision: AskDecision;
  /** how long it was allowed for, and for what (149) */
  how?: AskHow;
  /** the server said yes (false while the answer is on its way) */
  landed: boolean;
}
export type Reaction = InboxItem["reaction"];

export interface InboxState {
  items: InboxItem[];
  /** unseen in the whole inbox (the tab's number) */
  unseen: number;
  nextCursor: string | null;
  readAt: string | null;
  /** the first page has landed at least once */
  loaded: boolean;
  /** when the head was last read (ms), for stale-while-revalidate */
  fetchedAt: number;
  error: InboxError | null;
  /** an older page is on its way */
  paging: boolean;
  /** the last older page failed (the foot offers to try again) */
  pageError: boolean;
  /** ids with an unveil in flight */
  unveiling: readonly string[];
  /** ids dismissed here and not yet confirmed gone: a refresh racing the
   *  dismiss must not bring them back */
  gone: readonly string[];
  /** the private ring's asks (lib/self/ringView): null while the ring is not
   *  open to this viewer, or not read yet */
  ring: RingAsks | null;
  /** requests answered here this session, newest first */
  answered: readonly Answered[];
}

export const EMPTY: InboxState = Object.freeze({
  items: [],
  unseen: 0,
  nextCursor: null,
  readAt: null,
  loaded: false,
  fetchedAt: 0,
  error: null,
  paging: false,
  pageError: false,
  unveiling: [],
  gone: [],
  ring: null,
  answered: [],
}) as InboxState;

export type InboxAction =
  | { type: "head"; page: InboxPayload; at: number }
  | { type: "page"; page: InboxPayload }
  | { type: "error"; error: InboxError }
  | { type: "retry" }
  | { type: "paging"; on: boolean; failed?: boolean }
  | { type: "seen"; ids: readonly string[] }
  | { type: "unseen"; n: number }
  | { type: "item"; item: InboxItem }
  | { type: "unveiling"; id: string; on: boolean }
  | { type: "dismiss"; id: string }
  /** the server said no: lift the guard, and put the row back when it is handed in */
  | { type: "undismiss"; id: string; item?: InboxItem }
  | { type: "react"; id: string; reaction: Reaction }
  /** a record forgotten in its home (lib/you/record RECORD_FORGOTTEN_EVENT) */
  | { type: "forgotten"; kind: RecordKind; ids: readonly string[] }
  /** the ring's asks, read on their own (the quiet poll). undefined: keep what is held */
  | { type: "ring"; ring: RingAsks | null | undefined }
  /** the person answered a request: it leaves the waiting list at once */
  | { type: "answer"; id: string; decision: AskDecision; how?: AskHow }
  /** the server took the answer (or it was already decided elsewhere) */
  | { type: "answer-landed"; id: string }
  /** the answer did not go through: the request comes back as it was */
  | { type: "unanswer"; id: string }
  /** an answered row folded away */
  | { type: "answer-clear"; id: string };

/** How many requests wait on the person: they count with the unseen, beside the tab. */
export function waitingCount(s: Pick<InboxState, "ring">): number {
  return s.ring?.requests.length ?? 0;
}

/** The ring as the server read it, less what was answered here since (a read
 *  that began before an answer must not bring its request back). `incoming`
 *  undefined keeps what is held; null keeps it too (a read that failed costs
 *  the asks nothing). */
export function mergeRing(prev: RingAsks | null, incoming: RingAsks | null | undefined, answered: readonly Answered[], closed = false): RingAsks | null {
  if (closed) return null;
  if (incoming == null) return prev;
  const done = new Set(answered.map((a) => a.req.id));
  if (!done.size) return incoming;
  return {
    ...incoming,
    requests: incoming.requests.filter((r) => !done.has(r.id)),
    // an answered-once row already says its window aloud
    windows: incoming.windows.filter((w) => !done.has(w.requestId)),
  };
}

/** newest first in the server's own order: the time, then the id (one fold
 *  writes its arrivals in one insert, so a batch shares one time) */
export function newestFirst(a: InboxItem, b: InboxItem): number {
  if (a.at !== b.at) return a.at < b.at ? 1 : -1;
  return a.id === b.id ? 0 : a.id < b.id ? 1 : -1;
}

/** where a head page ends, as (time, id) */
export interface Edge {
  at: string;
  id: string;
}

/** strictly older than the edge, in that same order */
const olderThan = (i: InboxItem, e: Edge) => i.at < e.at || (i.at === e.at && i.id < e.id);

/** where a head page ends: its cursor (the last row the server read, even one
 *  it could not shape into an item), else its oldest item. null: nothing to
 *  measure by. */
export function edgeOf(fresh: readonly InboxItem[], cursor: string | null = null): Edge | null {
  const k = cursor ? cursor.lastIndexOf("~") : -1;
  if (cursor && k > 0 && k < cursor.length - 1 && /^\d{4}-\d{2}-\d{2}T/.test(cursor)) {
    return { at: cursor.slice(0, k), id: cursor.slice(k + 1).toLowerCase() };
  }
  if (!fresh.length) return null;
  const last = fresh.reduce((m, i) => (newestFirst(i, m) > 0 ? i : m));
  return { at: last.at, id: last.id };
}

/** a fresh copy of an item from the server, keeping what this page already
 *  knows better: an unveil or a seen that landed here after the read began. */
function keepLocal(fresh: InboxItem, prev: InboxItem | undefined): InboxItem {
  if (!prev) return fresh;
  let next = fresh;
  if (prev.seen && !fresh.seen) next = { ...next, seen: true };
  if (!prev.veiled && fresh.veiled) {
    next = { ...next, veiled: false, text: prev.text, title: prev.title, was: prev.was };
  }
  return next;
}

/** the head page is the truth for the span it covers (newest back to its
 *  edge, in (time, id) order): anything this page held inside that span that
 *  the server no longer lists was dismissed or forgotten elsewhere, so it
 *  goes. Older pages already loaded stay below it, rows that share the edge's
 *  time included, unless the head is the whole inbox (`complete`: the server
 *  said there is nothing older), when they go too. */
export function mergeHead(
  prev: readonly InboxItem[],
  fresh: readonly InboxItem[],
  gone: readonly string[] = [],
  complete = false,
  cursor: string | null = null,
): InboxItem[] {
  const drop = new Set(gone);
  const was = new Map(prev.map((i) => [i.id, i]));
  const head = fresh.filter((i) => !drop.has(i.id)).map((i) => keepLocal(i, was.get(i.id)));
  const edge = complete ? null : edgeOf(fresh, cursor);
  if (!edge) return head.sort(newestFirst);
  const inHead = new Set(fresh.map((i) => i.id));
  const tail = prev.filter((i) => !inHead.has(i.id) && !drop.has(i.id) && olderThan(i, edge));
  return [...head, ...tail].sort(newestFirst);
}

/** an older page joins below; an id already here keeps its copy. Sorted, so
 *  a page read down through a gap (more new arrivals than one page) lands
 *  each row in its place. */
export function mergeTail(prev: readonly InboxItem[], older: readonly InboxItem[], gone: readonly string[] = []): InboxItem[] {
  const have = new Set(prev.map((i) => i.id));
  const drop = new Set(gone);
  return [...prev, ...older.filter((i) => !have.has(i.id) && !drop.has(i.id))].sort(newestFirst);
}

/** the server's unseen count, less what this page has seen and not yet told it. */
function unseenAfter(serverUnseen: number, fresh: readonly InboxItem[], prev: readonly InboxItem[]): number {
  const seenHere = new Set(prev.filter((i) => i.seen).map((i) => i.id));
  const lag = fresh.filter((i) => !i.seen && seenHere.has(i.id)).length;
  return Math.max(0, serverUnseen - lag);
}

const without = (list: readonly string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : list);

export function reduce(s: InboxState, a: InboxAction): InboxState {
  switch (a.type) {
    case "head": {
      const fresh = a.page.items ?? [];
      const cursor = a.page.nextCursor ?? null;
      const items = mergeHead(s.items, fresh, s.gone, !cursor, cursor);
      // older pages already on screen below the head: keep paging from where
      // they ended, not from the head's own cursor. Only when the head reaches
      // down into what is held, though: more new arrivals than one page leave
      // a gap between the two, and the head's own cursor is the way down
      // through it (rows already held are read again and kept once).
      const edge = cursor ? edgeOf(fresh, cursor) : null;
      const newestHeld = s.items.length ? s.items.reduce((m, i) => (newestFirst(i, m) < 0 ? i : m)) : null;
      const meets = !!edge && !!newestHeld && !olderThan(newestHeld, edge);
      const hasTail = s.loaded && meets && items.some((i) => olderThan(i, edge!));
      return {
        ...s,
        items,
        unseen: unseenAfter(a.page.unseen ?? 0, fresh, s.items),
        nextCursor: hasTail ? s.nextCursor : cursor,
        readAt: a.page.readAt ?? null,
        loaded: true,
        fetchedAt: a.at,
        error: null,
        // a head without `ring` means the ring is closed to this viewer
        ring: mergeRing(s.ring, a.page.ring, s.answered, !("ring" in a.page)),
      };
    }
    case "page":
      return {
        ...s,
        items: mergeTail(s.items, a.page.items ?? [], s.gone),
        nextCursor: a.page.nextCursor ?? null,
        paging: false,
        pageError: false,
      };
    case "error":
      // a page already on screen outranks a failed refresh
      if (a.error === "failed" && s.loaded) return s;
      return { ...s, error: a.error };
    case "retry":
      return s.error ? { ...s, error: null } : s;
    case "paging":
      return { ...s, paging: a.on, pageError: a.on ? false : !!a.failed };
    case "seen": {
      const ids = new Set(a.ids);
      let turned = 0;
      const items = s.items.map((i) => {
        if (!ids.has(i.id) || i.seen) return i;
        turned++;
        return { ...i, seen: true };
      });
      if (!turned) return s;
      return { ...s, items, unseen: Math.max(0, s.unseen - turned) };
    }
    case "unseen":
      return s.unseen === Math.max(0, a.n) ? s : { ...s, unseen: Math.max(0, a.n) };
    case "item": {
      let hit = false;
      const items = s.items.map((i) => {
        if (i.id !== a.item.id) return i;
        hit = true;
        // the server's copy, but a seen this page already counted stays seen
        return i.seen && !a.item.seen ? { ...a.item, seen: true } : a.item;
      });
      return { ...s, items: hit ? items : s.items, unveiling: without(s.unveiling, a.item.id) };
    }
    case "unveiling":
      return {
        ...s,
        unveiling: a.on ? (s.unveiling.includes(a.id) ? s.unveiling : [...s.unveiling, a.id]) : without(s.unveiling, a.id),
      };
    case "dismiss": {
      const it = s.items.find((i) => i.id === a.id);
      return {
        ...s,
        items: s.items.filter((i) => i.id !== a.id),
        unseen: it && !it.seen ? Math.max(0, s.unseen - 1) : s.unseen,
        gone: s.gone.includes(a.id) ? s.gone : [...s.gone, a.id],
      };
    }
    case "undismiss": {
      const gone = without(s.gone, a.id);
      // the row comes back where it was, and its unseen with it
      if (!a.item || a.item.id !== a.id || s.items.some((i) => i.id === a.id)) return { ...s, gone };
      return {
        ...s,
        gone,
        items: [...s.items, a.item].sort(newestFirst),
        unseen: a.item.seen ? s.unseen : s.unseen + 1,
      };
    }
    case "react":
      return { ...s, items: s.items.map((i) => (i.id === a.id ? { ...i, reaction: a.reaction } : i)) };
    case "ring": {
      const ring = mergeRing(s.ring, a.ring, s.answered);
      return ring === s.ring ? s : { ...s, ring };
    }
    case "answer": {
      const req = s.ring?.requests.find((r) => r.id === a.id);
      if (!req || !s.ring) return s;
      return {
        ...s,
        ring: { ...s.ring, requests: s.ring.requests.filter((r) => r.id !== a.id) },
        answered: [{ req, decision: a.decision, ...(a.how ? { how: a.how } : {}), landed: false }, ...s.answered.filter((x) => x.req.id !== a.id)],
      };
    }
    case "answer-landed":
      return s.answered.some((x) => x.req.id === a.id && !x.landed)
        ? { ...s, answered: s.answered.map((x) => (x.req.id === a.id ? { ...x, landed: true } : x)) }
        : s;
    case "unanswer": {
      const was = s.answered.find((x) => x.req.id === a.id);
      if (!was) return s;
      const ring = s.ring ?? { requests: [], windows: [] };
      const requests = ring.requests.some((r) => r.id === a.id)
        ? ring.requests
        : [...ring.requests, was.req].sort((x, y) => (x.askedAt < y.askedAt ? 1 : x.askedAt > y.askedAt ? -1 : 0));
      return { ...s, ring: { ...ring, requests }, answered: s.answered.filter((x) => x.req.id !== a.id) };
    }
    case "answer-clear":
      return s.answered.some((x) => x.req.id === a.id) ? { ...s, answered: s.answered.filter((x) => x.req.id !== a.id) } : s;
    case "forgotten": {
      // nothing here may outlive a forget: every arrival that points at the
      // record goes, and with a fact every pattern read from it (the server
      // forgets those too). A read that began before the forget must not
      // bring them back (`gone`), and the next look reads the inbox again.
      const ids = new Set(a.ids.map((x) => x.toLowerCase()));
      const hit = (r: RecordRef | null | undefined) => !!r && r.kind === a.kind && ids.has(r.id.toLowerCase());
      const stays = (i: InboxItem) => !hit(i.record) && !(a.kind === "fact" && i.kind === "pattern" && !!i.restsOn?.refs.some(hit));
      const left = s.items.filter((i) => !stays(i));
      if (left.length === 0 && s.fetchedAt === 0) return s;
      if (left.length === 0) return { ...s, fetchedAt: 0 };
      const lost = left.filter((i) => !i.seen).length;
      return {
        ...s,
        items: s.items.filter(stays),
        unseen: Math.max(0, s.unseen - lost),
        gone: [...s.gone, ...left.map((i) => i.id).filter((id) => !s.gone.includes(id))],
        unveiling: s.unveiling.filter((id) => !left.some((i) => i.id === id)),
        fetchedAt: 0,
      };
    }
  }
}
